import { useCallback, useEffect, useState } from "react";
import firebase from "../../configs/FirebaseConfig";
import { useSelector } from "react-redux";

const db = firebase.firestore();

const collectionName = "products";

/**
 * Hook to fetch products from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount (default: true)
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {Array} options.productIds - Only fetch products with these document IDs (optional)
 * @returns {Object} - Products data and state
 */
export const useProducts = (options = {}) => {
  const { autoFetch = true, realtime = true, productIds = null } = options;
  const auth = useSelector((state) => state.firebaseReducer.auth);

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildQuery = useCallback(() => {
    return db.collection(collectionName).orderBy("name", "asc");
  }, []);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const snapshot = await buildQuery().get();

      if (snapshot.empty) {
        setProducts([]);
        return [];
      }

      let data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (productIds && productIds.length > 0) {
        const idSet = new Set(productIds);
        data = data.filter((p) => idSet.has(p.id));
      }

      setProducts(data);
      return data;
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    if (!autoFetch) return;

    if (auth.isEmpty) {
      setProducts([]);
      return;
    }

    if (realtime) {
      setIsLoading(true);

      const unsubscribe = buildQuery().onSnapshot(
        (snapshot) => {
          let data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          if (productIds && productIds.length > 0) {
            const idSet = new Set(productIds);
            data = data.filter((p) => idSet.has(p.id));
          }

          setProducts(data);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error("useProducts: Error in listener:", err);
          setError(err);
          setProducts([]);
          setIsLoading(false);
        },
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else {
      fetchProducts();
    }
  }, [autoFetch, realtime, fetchProducts, buildQuery, auth.isEmpty, productIds]);

  return {
    products,
    isLoading,
    error,
    fetchProducts,
  };
};
